import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Plus, Trash, GearSix, Tag, ChatCircle } from "@phosphor-icons/react";

export function ComercialConfig({ onBack }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const headers = { Authorization: `Bearer ${token}` };
  const [statusTypes, setStatusTypes] = useState([]);
  const [interactionTypes, setInteractionTypes] = useState([]);
  const [newStatus, setNewStatus] = useState({ name: "", label: "", color: "#3B82F6" });
  const [newInteraction, setNewInteraction] = useState({ name: "", color: "#3B82F6" });
  const [tab, setTab] = useState("status");

  const load = useCallback(async () => {
    try {
      const [sRes, iRes] = await Promise.all([
        axios.get(`${API}/comercial-status-types`, { headers }),
        axios.get(`${API}/comercial/interaction-types`, { headers }),
      ]);
      setStatusTypes(sRes.data);
      setInteractionTypes(iRes.data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const addStatus = async () => {
    if (!newStatus.name.trim() || !newStatus.label.trim()) return toast.error("Nombre y etiqueta requeridos");
    try {
      await axios.post(`${API}/comercial-status-types`, newStatus, { headers });
      toast.success("Estatus agregado"); setNewStatus({ name: "", label: "", color: "#3B82F6" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const deleteStatus = async (id) => {
    if (!window.confirm("Eliminar este estatus?")) return;
    try { await axios.delete(`${API}/comercial-status-types/${id}`, { headers }); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  const addInteraction = async () => {
    if (!newInteraction.name.trim()) return toast.error("Nombre requerido");
    try {
      await axios.post(`${API}/comercial/interaction-types`, { name: newInteraction.name, color: newInteraction.color }, { headers });
      toast.success("Tipo de interaccion agregado"); setNewInteraction({ name: "", color: "#3B82F6" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const deleteInteraction = async (id) => {
    if (!window.confirm("Eliminar?")) return;
    try { await axios.delete(`${API}/comercial/interaction-types/${id}`, { headers }); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-5" data-testid="comercial-config">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg">
          <GearSix size={20} className="text-slate-500" />
        </button>
        <h2 className="text-xl font-bold text-slate-900">Configuracion Comercial</h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button onClick={() => setTab("status")} className={`px-4 py-2 text-sm font-medium rounded-lg ${tab === "status" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}>
          <Tag size={16} className="inline mr-1" /> Tipos de Estatus
        </button>
        <button onClick={() => setTab("interactions")} className={`px-4 py-2 text-sm font-medium rounded-lg ${tab === "interactions" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}>
          <ChatCircle size={16} className="inline mr-1" /> Tipos de Interaccion
        </button>
      </div>

      {/* Status Types */}
      {tab === "status" && (
        <div className="bg-white rounded-xl border p-5 space-y-4">
          <h3 className="font-semibold text-slate-700">Tipos de Estatus</h3>
          <div className="space-y-2">
            {statusTypes.map(s => (
              <div key={s.id} className="flex items-center gap-3 py-2 px-3 bg-slate-50 rounded-lg">
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: s.color || "#64748b" }} />
                <span className="text-sm font-medium text-slate-800 flex-1">{s.label || s.name}</span>
                <span className="text-xs text-slate-400">{s.name}</span>
                {isAdmin && (
                  <button onClick={() => deleteStatus(s.id)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                )}
              </div>
            ))}
          </div>
          {isAdmin && (
            <div className="flex gap-2 items-end border-t pt-3">
              <div className="flex-1">
                <label className="text-xs text-slate-500">ID (sin espacios)</label>
                <Input value={newStatus.name} onChange={e => setNewStatus(p => ({ ...p, name: e.target.value.replace(/\s/g, "_").toLowerCase() }))} placeholder="ej: seguimiento" className="mt-0.5" data-testid="new-status-name" />
              </div>
              <div className="flex-1">
                <label className="text-xs text-slate-500">Etiqueta</label>
                <Input value={newStatus.label} onChange={e => setNewStatus(p => ({ ...p, label: e.target.value }))} placeholder="ej: En Seguimiento" className="mt-0.5" data-testid="new-status-label" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Color</label>
                <input type="color" value={newStatus.color} onChange={e => setNewStatus(p => ({ ...p, color: e.target.value }))} className="mt-0.5 w-10 h-9 rounded border cursor-pointer" />
              </div>
              <Button size="sm" onClick={addStatus} data-testid="add-status-btn"><Plus size={16} className="mr-1" /> Agregar</Button>
            </div>
          )}
        </div>
      )}

      {/* Interaction Types */}
      {tab === "interactions" && (
        <div className="bg-white rounded-xl border p-5 space-y-4">
          <h3 className="font-semibold text-slate-700">Tipos de Interaccion</h3>
          <p className="text-xs text-slate-500">Configura los tipos de interaccion disponibles: visita presencial, llamada, correo, demostracion, etc.</p>
          <div className="space-y-2">
            {interactionTypes.map(t => (
              <div key={t.id} className="flex items-center gap-3 py-2 px-3 bg-slate-50 rounded-lg">
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: t.color || "#64748b" }} />
                <span className="text-sm font-medium text-slate-800 flex-1">{t.name}</span>
                {isAdmin && !["visita", "llamada", "whatsapp", "correo", "demo"].includes(t.id) && (
                  <button onClick={() => deleteInteraction(t.id)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                )}
              </div>
            ))}
          </div>
          {isAdmin && (
            <div className="flex gap-2 items-end border-t pt-3">
              <div className="flex-1">
                <label className="text-xs text-slate-500">Nombre</label>
                <Input value={newInteraction.name} onChange={e => setNewInteraction(p => ({ ...p, name: e.target.value }))} placeholder="ej: Reunion Virtual" className="mt-0.5" data-testid="new-interaction-name" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Color</label>
                <input type="color" value={newInteraction.color} onChange={e => setNewInteraction(p => ({ ...p, color: e.target.value }))} className="mt-0.5 w-10 h-9 rounded border cursor-pointer" />
              </div>
              <Button size="sm" onClick={addInteraction} data-testid="add-interaction-btn"><Plus size={16} className="mr-1" /> Agregar</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
